<script setup>
import getPosts from "~/graphql/getPosts.gql"
import PostMetaDetails from "~/components/blog/PostMetaDetails.vue"
import LoadingAnimation from "~/components/utils/LoadingAnimation.vue"
import PostPagination from "~/components/blog/PostPagination.vue"

const numArticlesPerPage = 20
const localePath = useLocaleRoute()
let fetchedPostsResponse = ref([])

const props = defineProps({
  page: { type: Number, required: true },
})

const totalPages = computed(() => {
  const maximumOffset = fetchedPostsResponse?.posts?.pageInfo?.offsetPagination?.total
  if (!maximumOffset) {
    return 0
  }
  return Math.ceil(maximumOffset / numArticlesPerPage)
})

const offset = computed(() => {
  return (props.page - 1) * numArticlesPerPage
})

const { data, pending } = await useAsyncQuery(getPosts, { size: numArticlesPerPage, offset: offset.value })
fetchedPostsResponse = data.value
</script>

<template>
  <section id="post-list-section">
    <div v-if="!pending && fetchedPostsResponse?.posts?.nodes" class="post-list">
      <div v-for="(post, index) in fetchedPostsResponse.posts.nodes" :key="post.id" class="post-in-list">
        <NuxtLink class="post-title-link" :to="localePath('/blog/' + post.slug)">
          <h2 class="is-size-4">
            {{ post.title }}
          </h2>
        </NuxtLink>
        <PostMetaDetails
          :author-first-name="post.author.node.firstName"
          :author-last-name="post.author.node.lastName"
          :date="post.date"
          :has-duration="false"
        />
        <div class="columns is-vcentered is-centered">
          <div class="preview-column column is-one-quarter is-one-third-mobile has-text-centered">
            <img
              v-if="Boolean(post.featuredImage)"
              class="preview-image"
              sizes="sm:200px md:400px lg:400px"
              :src="'https://memblog.de' + post.featuredImage.node.uri"
              :alt="post.featuredImage.node.altText"
            />
            <img
              v-else
              class="preview-image"
              sizes="sm:200px md:400px lg:400px"
              src="~assets/images/memodio_logos/logo_icon_purple.svg"
            />
          </div>
          <div class="column is-three-quarters">
            <!-- eslint-disable vue/no-v-html -->
            <div class="post-preview" v-html="cleanUpExcerpt(post.excerpt)" />
            <!-- eslint-enable vue/no-v-html -->
            <NuxtLink class="continue-reading" :to="localePath({ name: 'Blog-Slug', params: { slug: post.slug } })">
              Weiterlesen
            </NuxtLink>
          </div>
        </div>
        <hr v-if="index !== fetchedPostsResponse.posts.nodes - 1" class="line-divider" />
      </div>
      <PostPagination
        v-if="totalPages > 1"
        :per-page="numArticlesPerPage"
        :total-pages="totalPages"
        :current-page="page"
      />
    </div>

    <div v-else>
      <LoadingAnimation :has-text="false" />
    </div>
  </section>
</template>

<script>
export default {
  name: "PostList",
  fetchKey: "postListFetchKey",
  methods: {
    cleanUpExcerpt(excerpt) {
      const regexpSplitByParagraphs = /<p>.*<\/p>/g
      const firstParagraph = regexpSplitByParagraphs.exec(excerpt)[0]
      // TODO: Sanitize
      return firstParagraph
    },
  },
}
</script>

<style lang="scss" scoped>
#post-list-section {
  padding-top: 48px;
}

.line-divider {
  border: solid 1px #9c8573;
}

.preview-image {
  border-radius: 16px;
  width: 200px;
  height: 140px;
  object-fit: cover;

  @media (max-width: $tablet) {
    width: 400px;
    max-height: 220px;
  }
}

.preview-column {
  @media (max-width: $mobile) {
    display: none;
  }
}

@media (max-width: 800px) {
  .post-list {
    margin-top: -2rem !important;
  }

  h3 {
    font-size: 24px;
  }
}

.columns {
  display: flex;
}

.post-list {
  display: grid;
  gap: 24px;
  justify-items: center;
  margin: 2rem 0 0;
  padding-bottom: 3rem;
}

.post-in-list {
  max-width: 1140px;
  padding-right: 15px;
  padding-left: 15px;
}

.post-in-list .post-title-link h3 {
  color: #000000;
  margin-bottom: 0;
  line-height: 1;
}

.post-in-list .post-title-link h3:hover {
  color: $brandpurple;
  text-decoration: none;
}

.post-preview::v-deep(p) {
  color: black;
  size: 1rem;
}

.continue-reading {
  color: $brandpurple;
  size: 1rem;
}
.continue-reading:hover {
  color: $brandpurple;
}
</style>
